import { useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, RefreshCw, Clock, FileText, CheckCircle, AlertTriangle, XCircle, Settings, User, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { usePhaseAccess } from "@/hooks/usePhaseRoles";
import { usePhaseTimer } from "@/hooks/usePhaseTimer";
import { useAppliance } from "@/hooks/useAppliance";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useEffect } from "react";
import { useWorkflowRunner } from "@/workflows/runnerContext";

interface AnswerOption {
  id: string;
  label: string;
  score: number;
  maxScore: number;
  status: "good" | "warning" | "bad";
  description: string;
}

interface Question {
  id: string;
  category: string;
  isCritical: boolean;
  question: string;
  options: AnswerOption[];
}

const baseQuestions: Question[] = [
  { id: "1", category: "Age & Model Assessment", isCritical: true, question: "What is the approximate age of this appliance?", options: [
    { id: "a", label: "0-2 years", score: 10, maxScore: 10, status: "good", description: "Very recent model, still under warranty period" },
    { id: "b", label: "3-5 years", score: 9, maxScore: 10, status: "good", description: "Recent model, parts readily available" },
    { id: "c", label: "6-8 years", score: 7, maxScore: 10, status: "warning", description: "Moderate age, most parts still available" },
    { id: "d", label: "9-12 years", score: 5, maxScore: 10, status: "warning", description: "Older model, some parts may be limited" },
    { id: "e", label: "13-15 years", score: 3, maxScore: 10, status: "bad", description: "Legacy model, limited parts availability" },
    { id: "f", label: "Over 15 years", score: 0, maxScore: 10, status: "bad", description: "Obsolete model, parts likely unavailable" },
  ]},
  { id: "2", category: "Structural Assessment", isCritical: true, question: "What is the overall structural condition of the appliance?", options: [
    { id: "a", label: "Excellent", score: 10, maxScore: 10, status: "good", description: "No visible damage, all panels intact" },
    { id: "b", label: "Good", score: 8, maxScore: 10, status: "good", description: "Minor cosmetic issues only" },
    { id: "c", label: "Fair", score: 6, maxScore: 10, status: "warning", description: "Some dents or scratches, but structurally sound" },
    { id: "d", label: "Poor", score: 3, maxScore: 10, status: "bad", description: "Significant damage but repairable" },
    { id: "e", label: "Critical", score: 0, maxScore: 10, status: "bad", description: "Structural damage, safety concerns" },
  ]},
  { id: "3", category: "Electrical Assessment", isCritical: true, question: "How would you assess the electrical system condition?", options: [
    { id: "a", label: "Fully Functional", score: 10, maxScore: 10, status: "good", description: "All electrical components work properly" },
    { id: "b", label: "Minor Issues", score: 7, maxScore: 10, status: "good", description: "Some buttons or lights not working" },
    { id: "c", label: "Moderate Issues", score: 5, maxScore: 10, status: "warning", description: "Control panel issues but motor works" },
    { id: "d", label: "Major Issues", score: 2, maxScore: 10, status: "bad", description: "Motor or main electrical problems" },
    { id: "e", label: "Non-Functional", score: 0, maxScore: 10, status: "bad", description: "No electrical response, safety hazard" },
  ]},
  { id: "4", category: "Mechanical Assessment", isCritical: false, question: "What is the condition of mechanical components (drum, bearings, pump)?", options: [
    { id: "a", label: "Excellent", score: 10, maxScore: 10, status: "good", description: "Smooth operation, no unusual sounds" },
    { id: "b", label: "Good", score: 8, maxScore: 10, status: "good", description: "Minor wear but functional" },
    { id: "c", label: "Moderate Wear", score: 6, maxScore: 10, status: "warning", description: "Some noise or vibration" },
    { id: "d", label: "Significant Wear", score: 3, maxScore: 10, status: "bad", description: "Loud noises, requires major repair" },
    { id: "e", label: "Failed", score: 0, maxScore: 10, status: "bad", description: "Seized bearings or broken drum" },
  ]},
  { id: "5", category: "Seals & Hoses", isCritical: false, question: "Assess the condition of seals, gaskets, and hoses:", options: [
    { id: "a", label: "Like New", score: 10, maxScore: 10, status: "good", description: "No cracks, tears, or deterioration" },
    { id: "b", label: "Minor Wear", score: 8, maxScore: 10, status: "good", description: "Slight discoloration but intact" },
    { id: "c", label: "Moderate Wear", score: 6, maxScore: 10, status: "warning", description: "Some cracking or hardening" },
    { id: "d", label: "Replacement Needed", score: 4, maxScore: 10, status: "bad", description: "Tears or leaks present" },
    { id: "e", label: "Multiple Failures", score: 1, maxScore: 10, status: "bad", description: "Multiple seals compromised" },
  ]},
];

const statusIcons = { good: CheckCircle, warning: AlertTriangle, bad: XCircle };
const statusColors = { good: "text-refurbish", warning: "text-repair", bad: "text-destructive" };

const RefurbishmentTriage = () => {
  const { t } = useTranslation("refurb");
  const { id } = useParams();
  const navigate = useNavigate();
  const runner = useWorkflowRunner();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [observations, setObservations] = useState("");

  const { appliance: loadedAppliance, notFound } = useAppliance(id);

  useEffect(() => {
    if (notFound) {
      toast.error(t("common.applianceNotFound"));
      navigate("/work-orders");
    }
  }, [notFound, navigate, t]);

  const appliance = loadedAppliance ?? { name: t("common.loading"), modelNumber: "", category: "Appliance" };

  const { canAccessPhase, isLoading: accessLoading } = usePhaseAccess(appliance.category);
  const canAccessTriage = canAccessPhase("triage");

  usePhaseTimer({
    phaseCategory: "triage_assessment",
    phaseLabel: "Triage Assessment",
    applianceId: id,
    applianceType: appliance.category,
    enabled: !accessLoading && canAccessTriage,
  });

  const questions = baseQuestions;
  const question = questions[currentQuestion];
  const progress = ((currentQuestion + 1) / questions.length) * 100;
  const selectedAnswer = answers[question.id];

  const handleSelectAnswer = (optionId: string) => {
    setAnswers((prev) => ({ ...prev, [question.id]: optionId }));
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) setCurrentQuestion((prev) => prev + 1);
    else if (runner) runner.complete({ answers });
    else navigate(`/appliance/${id}/refurbish/results`);
  };

  const handleBack = () => {
    if (currentQuestion > 0) setCurrentQuestion((prev) => prev - 1);
    else if (runner) runner.back();
    else navigate(`/appliance/${id}`);
  };

  return (
    <div className="min-h-screen bg-background">
      {!accessLoading && !canAccessTriage && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex flex-col items-center justify-center gap-4">
          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center">
            <Lock className="w-8 h-8 text-muted-foreground" />
          </div>
          <h2 className="text-xl font-bold text-foreground">{t("triage.accessRestricted")}</h2>
          <p className="text-sm text-muted-foreground text-center max-w-sm">{t("triage.accessRestrictedDesc")}</p>
          <Button variant="outline" onClick={() => navigate(`/appliance/${id}`)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t("common.backToProduct")}
          </Button>
        </div>
      )}
      <header className="flex items-center justify-between px-6 py-4 bg-card border-b border-border">
        <div className="flex items-center gap-6">
          <button onClick={() => navigate("/work-orders")} className="flex items-center gap-2 text-primary hover:text-primary/80 transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">{t("common.backToWorkOrders")}</span>
          </button>
          <div className="flex items-center gap-2">
            <div>
              <h1 className="font-bold text-foreground">FixID</h1>
              <div className="flex items-center gap-1 text-sm text-muted-foreground">
                <RefreshCw className="w-3 h-3" />
                <span>{t("triage.subtitleNav")}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-6">
          <div className="text-right">
            <p className="font-medium text-foreground">{appliance.name}</p>
            <div className="flex items-center gap-2 justify-end">
              <span className="text-sm text-muted-foreground">{appliance.modelNumber}</span>
              <span className="category-badge category-badge-washing text-xs">{appliance.category}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button className="p-2 rounded-lg hover:bg-muted transition-colors"><Settings className="w-5 h-5 text-muted-foreground" /></button>
            <button className="p-2 rounded-lg hover:bg-muted transition-colors"><User className="w-5 h-5 text-muted-foreground" /></button>
          </div>
        </div>
      </header>

      <main className="p-6 max-w-4xl mx-auto">
        <div className="bg-card rounded-2xl border border-border p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <RefreshCw className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-foreground">{t("triage.title")}</h2>
                <p className="text-muted-foreground">{t("triage.subtitle")}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-muted-foreground">
              <Clock className="w-4 h-4" />
              <span className="text-sm">{t("triage.duration")}</span>
            </div>
          </div>
        </div>

        <div className="bg-card rounded-2xl border border-border p-6 mb-6">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-muted-foreground">{t("triage.questionOf", { current: currentQuestion + 1, total: questions.length })}</span>
            <span className="text-sm text-muted-foreground">{t("triage.percentComplete", { pct: Math.round(progress) })}</span>
          </div>
          <Progress value={progress} className="h-2 mb-4" />
          <div className="flex items-center justify-between">
            <Button variant="outline" onClick={handleBack}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              {currentQuestion === 0 ? t("common.backToProduct") : t("triage.previous")}
            </Button>
            <Button onClick={handleNext} disabled={!selectedAnswer} className="bg-primary hover:bg-primary/90">
              {currentQuestion < questions.length - 1 ? t("triage.nextQuestion") : t("triage.completeAssessment")}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
          {!selectedAnswer && (
            <div className="flex items-center gap-2 text-muted-foreground text-sm mt-3">
              <AlertTriangle className="w-4 h-4" />
              {t("triage.selectToContinue")}
            </div>
          )}
        </div>

        <div className="bg-card rounded-2xl border border-border p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <span className="px-3 py-1 bg-foreground text-background text-sm font-medium rounded-full">{question.category}</span>
            {question.isCritical && (
              <span className="px-3 py-1 bg-destructive/10 text-destructive text-sm font-medium rounded-full flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                {t("triage.criticalAssessment")}
              </span>
            )}
          </div>

          <h3 className="text-lg font-semibold text-foreground mb-4">{question.question}</h3>

          <div className="space-y-3">
            {question.options.map((option) => {
              const StatusIcon = statusIcons[option.status];
              const isSelected = selectedAnswer === option.id;
              return (
                <button
                  key={option.id}
                  onClick={() => handleSelectAnswer(option.id)}
                  className={`w-full flex items-center gap-4 p-4 rounded-xl border transition-colors text-left ${
                    isSelected ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-primary/50 bg-background"
                  }`}
                >
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${isSelected ? "border-primary-foreground" : "border-muted-foreground"}`}>
                    {isSelected && (<div className="w-2.5 h-2.5 rounded-full bg-primary-foreground" />)}
                  </div>
                  <div className="flex items-center gap-4 flex-wrap">
                    <span className={`font-semibold ${isSelected ? "text-primary-foreground" : "text-foreground"}`}>{option.label}</span>
                    <span className={`text-sm ${isSelected ? "text-primary-foreground/80" : "text-muted-foreground"}`}>
                      {t("triage.score", { score: option.score, max: option.maxScore })}
                    </span>
                    <div className={`flex items-center gap-1 ${isSelected ? "text-primary-foreground/80" : statusColors[option.status]}`}>
                      <StatusIcon className="w-4 h-4" />
                      <span className="text-sm">{option.description}</span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="bg-card rounded-2xl border border-border p-6 mb-6">
          <div className="flex items-center gap-2 mb-3">
            <FileText className="w-5 h-5 text-muted-foreground" />
            <h4 className="font-medium text-foreground">{t("triage.additionalObservations")}</h4>
          </div>
          <Textarea
            value={observations}
            onChange={(e) => setObservations(e.target.value)}
            placeholder={t("triage.observationsPlaceholder")}
            className="min-h-[80px] bg-background"
          />
        </div>
      </main>
    </div>
  );
};

export default RefurbishmentTriage;
